import { useCallback, useEffect, useRef } from "react";

import { useUiFlag } from "../../hooks/useUiFlag";
import { runListingTour } from "./listingTour";

export interface UseProjectListingTourOptions {
  /**
   * True once the split view has projects and a drawn map. The tour's first
   * step points at the map, so starting against a blank pane would waste the
   * one first-run impression.
   */
  ready: boolean;
}

export interface UseProjectListingTourResult {
  /** Replay on demand (the toolbar's ? button). */
  start: () => void;
  /** True before the first run has been offered. */
  neverRun: boolean;
}

/**
 * Auto-runs the listing coach tour on a person's first visit to the new
 * layout, and exposes a manual replay. Same shape as useStoryBuilderTour:
 * the "seen it" marker is a server-side ui_flag so it follows the person,
 * and skipping counts as seen.
 */
export function useProjectListingTour({
  ready,
}: UseProjectListingTourOptions): UseProjectListingTourResult {
  const { value, loading, set } = useUiFlag("projectListingTour");
  const startedRef = useRef(false);
  const stopRef = useRef<(() => void) | null>(null);

  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    stopRef.current = runListingTour((outcome) => {
      stopRef.current = null;
      startedRef.current = false;
      set(outcome);
    });
  }, [set]);

  useEffect(() => {
    if (!ready || loading || value !== null || startedRef.current) return;
    // Give the panes a moment to lay out: driver.js measures the highlight
    // box from real geometry.
    const timer = window.setTimeout(start, 600);
    return () => window.clearTimeout(timer);
  }, [ready, loading, value, start]);

  // Leaving the page mid-tour must take the overlay with it.
  useEffect(() => () => stopRef.current?.(), []);

  return { start, neverRun: value === null };
}
